"use client";

import { Loader2, Mail, RefreshCw, Trash2, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { Dialog } from "@/components/shared/Dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackCalendarSharing } from "@/lib/analytics";
import type { SharedCalendarInfo } from "@/lib/types";
import { sharingSchemas, validate } from "@/lib/validation";

interface ShareCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ShareCalendarModal({ isOpen, onClose }: ShareCalendarModalProps) {
  const t = useTranslations("calendar");
  const [email, setEmail] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [shares, setShares] = useState<SharedCalendarInfo[]>([]);

  const fetchShares = useCallback(async () => {
    try {
      const response = await fetch("/api/calendar/share");
      const data = await response.json();
      if (response.ok) {
        setShares(
          data.shares.map((s: any) => ({
            email: s.email,
            accepted: s.accepted,
            invitationId: s.id,
            acceptedAt: s.acceptedAt,
          }))
        );
      }
    } catch (err) {
      console.error("Failed to fetch calendar shares:", err);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchShares();
    }
  }, [isOpen, fetchShares]);

  const handleShare = async () => {
    const trimmedEmail = email.trim();

    const validationResult = validate(sharingSchemas.email, trimmedEmail);
    if (!validationResult.success) {
      const errorKey = validationResult.error?.replace(/^validation\./, "") || "invalidEmail";
      toast.error(t(errorKey as any));
      return;
    }

    setIsSharing(true);

    try {
      const response = await fetch("/api/calendar/share", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(t(data.error as any) || t("failedToShareCalendar"));
        return;
      }

      trackCalendarSharing("invitation_sent");

      toast.success(t("calendarInvitationSent", { email: trimmedEmail }), {
        description: data.invitation.emailSent ? t("emailDelivered") : t("emailNotConfigured"),
      });

      setEmail("");
      fetchShares();
    } catch (err) {
      toast.error(t("failedToShareCalendar"));
      console.error(err);
    } finally {
      setIsSharing(false);
    }
  };

  const handleResend = async (invitationId: string, recipientEmail: string) => {
    setResendingId(invitationId);

    try {
      const response = await fetch("/api/calendar/share/resend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invitationId }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(t(data.error as any) || t("failedToResend"));
        return;
      }

      toast.success(t("calendarInvitationResent"), {
        description: t("emailSentTo", { email: recipientEmail }),
      });
    } catch (err) {
      toast.error(t("failedToResend"));
      console.error(err);
    } finally {
      setResendingId(null);
    }
  };

  const handleUnshare = async (invitationId: string, recipientEmail: string) => {
    setRemovingId(invitationId);

    try {
      const response = await fetch("/api/calendar/share", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invitationId }),
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(t(data.error as any) || t("failedToRemove"));
        return;
      }

      fetchShares();

      toast.success(t("calendarShareRemoved"), {
        description: t("noLongerHasAccess", { email: recipientEmail }),
      });
    } catch (err) {
      toast.error(t("failedToRemove"));
      console.error(err);
    } finally {
      setRemovingId(null);
    }
  };

  const isAnyLoading = isSharing || resendingId !== null || removingId !== null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t("shareCalendar")}
      description={t("shareCalendarSubtitle")}
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="calendar-share-email">{t("inviteByEmail")}</Label>
          <div className="flex gap-2 mt-2">
            <Input
              id="calendar-share-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("emailPlaceholder")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleShare();
                }
              }}
              disabled={isAnyLoading}
            />
            <Button
              onClick={handleShare}
              disabled={isAnyLoading || !email.trim()}
              className="flex-shrink-0 gap-1"
            >
              {isSharing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  {t("send")}
                </>
              )}
            </Button>
          </div>
        </div>

        {shares.length > 0 && (
          <div>
            <h4 className="mb-2 flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              {t("peopleWithAccess", { count: shares.length })}
            </h4>
            <div className="space-y-2">
              {shares.map((share) => (
                <div
                  key={share.invitationId}
                  className="flex items-center justify-between gap-2 p-3 bg-secondary/50 rounded-lg"
                >
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <span className="text-sm truncate">{share.email}</span>
                    {share.accepted ? (
                      <span className="text-xs text-green-600 dark:text-green-500">
                        {t("accepted")}
                      </span>
                    ) : (
                      <span className="text-xs text-yellow-600 dark:text-yellow-500">
                        {t("pending")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!share.accepted && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResend(share.invitationId, share.email)}
                        disabled={isAnyLoading}
                        title={t("resendInvitation")}
                      >
                        {resendingId === share.invitationId ? (
                          <Loader2 className="size-5 animate-spin text-muted-foreground" />
                        ) : (
                          <RefreshCw className="size-5 text-muted-foreground" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnshare(share.invitationId, share.email)}
                      disabled={isAnyLoading}
                      title={t("removeShare")}
                    >
                      {removingId === share.invitationId ? (
                        <Loader2 className="size-5 animate-spin text-destructive" />
                      ) : (
                        <Trash2 className="size-5 text-destructive" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
